<script lang="ts" setup>
import BasicButton from '~/components/form-elements/BasicButton.vue'
import BasicTextarea from '~/components/form-elements/BasicTextarea.vue'
import RadioButtons from '~/components/form-elements/RadioButtons.vue'
import StarRating from '~/components/form-elements/StarRating.vue'
import CustomModalWindow from '~/components/modal-windows/CustomModalWindow.vue'
import type { RadioButtonChoice } from '~/types/form-elements'

const model = defineModel<boolean>()

const props = defineProps<{
  productId: string
  reviewId: string | null
}>()

const emit = defineEmits<{
  (e: 'setUserReviewId', x: string): void
}>()

const emptyForm = { advantages: null, comment: null, disadvantages: null, isAnonymous: false, score: 0 }
const isAnonymousChoices: RadioButtonChoice[] = [
  { labelText: 'Под никнеймом из\x0Aнастроек личного кабинета', value: false },
  { labelText: 'Как анонимный покупатель', value: true }
]
const messages = {
  failedToGetReview: 'Не удалось получить отзыв.',
  failedToSendReview: 'Не удалось отправить отзыв.',
  reviewIsSent: 'Отзыв отправлен на проверку.',
  scoreIsNotSet: 'Необходимо поставить оценку.'
}

const { $apiHelper, $toast } = useNuxtApp()

const form = ref<any>({ ...emptyForm })
const hasUnsavedChanges = ref(false)
const id = ref<string | null>(null)
const isDataLoading = ref(false)
const isDataLoadingError = ref(false)
const isLoading = ref(false)

async function beforeOpen (): Promise<void> {
  id.value = props.reviewId

  if (id.value !== null) {
    await loadData()
  }
}

function close (): void {
  model.value = false
}

async function saveChanges (): Promise<void> {
  const { advantages, comment, disadvantages, isAnonymous, score } = form.value
  const { failedToSendReview, reviewIsSent, scoreIsNotSet } = messages

  if (score === 0) {
    $toast.error(scoreIsNotSet)

    return
  }

  const data: any = { advantages, comment, disadvantages, isAnonymous: isAnonymous ? 'yes' : 'no', score }

  if (id.value === null) {
    data.productId = props.productId
  }

  isLoading.value = true

  try {
    if (id.value === null) {
      const { data: dataFromResponse } = await $apiHelper.reviews.createReview(data)
      const { id: userReviewId } = dataFromResponse
      id.value = userReviewId
      emit('setUserReviewId', userReviewId)
    } else {
      await $apiHelper.reviews.updateReview(id.value, data)
    }

    $toast.success(reviewIsSent)

    hasUnsavedChanges.value = false

    close()
  } catch (error) {
    console.error(error)

    $toast.error(failedToSendReview)
  }

  isLoading.value = false
}

async function loadData (): Promise<void> {
  isDataLoading.value = true
  isDataLoadingError.value = false

  try {
    const { data } = await $apiHelper.reviews.getReview(id.value as string)
    const { advantages, comment, disadvantages, isAnonymous, score } = data.attributes
    form.value.advantages = advantages
    form.value.comment = comment
    form.value.disadvantages = disadvantages
    form.value.isAnonymous = isAnonymous
    form.value.score = score
  } catch (error) {
    console.error(error)

    isDataLoadingError.value = true

    $toast.error(messages.failedToGetReview)
  }

  isDataLoading.value = false
}
</script>

<template>
  <CustomModalWindow
    v-model="model"
    :has-unsaved-changes="hasUnsavedChanges"
    prevent-unsaved-changes
    size="large"
    @before-open="beforeOpen"
  >
    <template #title>
      {{ id === null ? 'Создание' : 'Редактирование' }} отзыва о&nbsp;товаре
    </template>

    <Preloader v-if="isDataLoading" />
    <Notification v-else-if="isDataLoadingError" status="error">
      {{ messages.failedToGetReview }}
    </Notification>
    <template v-else>
      <form class="mb-6" @change="hasUnsavedChanges = true" @submit.prevent>
        <Notification class="mb-6" is-small status="info">
          Все отзывы проходят проверку перед публикацией. Их&nbsp;можно изменять и&nbsp;удалять.
        </Notification>

        <StarRating v-model="form.score" class="mb-6" is-required label-text="Оценка" />

        <BasicTextarea
          v-model="form.advantages"
          class="mb-6"
          label-text="Достоинства (по желанию)"
          :max-length="4096"
        />

        <BasicTextarea
          v-model="form.disadvantages"
          class="mb-6"
          label-text="Недостатки (по желанию)"
          :max-length="4096"
        />

        <BasicTextarea
          v-model="form.comment"
          class="mb-6"
          label-text="Комментарий (по желанию)"
          :max-length="4096"
        />

        <RadioButtons
          v-model="form.isAnonymous"
          :choices="isAnonymousChoices"
          is-vertical
          label-text="Как оставить отзыв"
          name="isAnonymous"
        />
      </form>

      <BasicButton
        color="green"
        :is-loading="isLoading"
        :title="id === null ? 'Отправить' : 'Сохранить изменения'"
        @click="saveChanges"
      />
    </template>

    <template v-if="!isDataLoading" #action>
      <a v-if="isDataLoadingError" href="#" @click.prevent="loadData">Повторить попытку</a>
      <a v-else href="#" @click.prevent="close">Закрыть</a>
    </template>
  </CustomModalWindow>
</template>
